// src/web-app/auth/auth-handler.js
import { startFootcheckApp, authenticateWithEmail, createAccountWithEmail } from "../lib/auth/bootstrap.js";
import { signInWithGoogle, getAuthInstance } from "../lib/auth/firebase.js";
import { getAccessToken, getClientId as getAuthClientId, logoutEverywhere, resetAuthState } from "../lib/auth/session.js";

export function getAuth() {
  return getAuthInstance();
}

// Global auth state management
let authReady = false;
let authReadyResolve;
let authReadyPromise = new Promise((resolve) => {
  authReadyResolve = resolve;
});
let initializingAuth = false; // Guard to prevent duplicate initialization

export function getAuthReadyPromise() {
  return authReadyPromise;
}

export function setAuthReady(ready) {
  authReady = ready;
  if (ready && authReadyResolve) {
    authReadyResolve();
  }
}

export function resetAuthReadyPromise() {
  authReady = false;
  authReadyPromise = new Promise((resolve) => {
    authReadyResolve = resolve;
  });
}

export function isAuthReady() {
  return authReady;
}

// Initialize authentication
export async function initializeAuth(onSuccess, onError) {
  // Prevent duplicate initialization
  if (initializingAuth) {
    console.warn('[AUTH] initializeAuth already in progress, skipping duplicate call');
    return;
  }
  if (authReady) {
    console.log('[AUTH] Authentication already ready, calling onSuccess');
    if (onSuccess) onSuccess();
    return;
  }
  
  initializingAuth = true;
  const loadingOverlay = document.getElementById('loading-overlay');
  const loadingMessage = document.querySelector('.loading-message');
  
  try {
    console.log('[STARTUP] Web app initialization starting at', new Date().toISOString());
    loadingMessage.textContent = 'Authenticating...';
    
    await startFootcheckApp();
    console.log('[STARTUP] startFootcheckApp completed successfully');
    
    // Expose auth functions for use by common modules (api.js)
    window.footcheckAuth = {
      getClientId: getAuthClientId,
      getAccessToken: getAccessToken
    };
    
    authReady = true;
    console.log('[AUTH] Authentication ready, client_id:', getAuthClientId());
    authReadyResolve(); // Signal that auth is ready
    
    // Hide loading overlay
    loadingMessage.textContent = 'Ready!';
    setTimeout(() => {
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }
    }, 300);
    
    if (onSuccess) onSuccess();
  } catch (e) {
    console.error("[STARTUP] Failed to initialize authentication at", new Date().toISOString(), "- Error:", e);
    console.error("[STARTUP] Error message:", e.message);
    console.error("[STARTUP] Error stack:", e.stack);
    
    // Hide loading overlay before showing auth form
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
    
    console.log("[STARTUP] Showing auth form due to initialization failure");
    if (onError) onError();
  } finally {
    initializingAuth = false;
  }
}

export async function handleLogout() {
  try {
    console.log('[AUTH] Logging out...');
    
    // Call logout function to revoke sessions and clear local storage
    await logoutEverywhere();
    
    // Reset auth state
    resetAuthState();
    authReady = false;
    
    // Create a new promise for next login
    resetAuthReadyPromise();
    
    // Clear exposed auth functions
    window.footcheckAuth = null;
    
    // Hide user info
    const userInfoEl = document.getElementById('user-info');
    if (userInfoEl) {
      userInfoEl.style.display = 'none';
    }
    
    // Clear status message
    const statusEl = document.getElementById('status');
    if (statusEl) {
      statusEl.textContent = 'Logged out successfully';
    }
    
    console.log('[AUTH] Logout complete');
    return true;
  } catch (error) {
    console.error('[AUTH] Logout error:', error);
    alert('Failed to log out: ' + error.message);
    return false;
  }
}

export async function handleSignIn(email, password) {
  if (!email || !password) {
    throw new Error('Please enter both email and password');
  }
  
  const statusEl = document.getElementById('status');
  statusEl.textContent = 'Signing in...';
  statusEl.style.color = 'var(--muted)';
  
  await authenticateWithEmail(email, password);
  
  // Set up auth functions for the UI
  window.footcheckAuth = {
    getClientId: getAuthClientId,
    getAccessToken: getAccessToken
  };
  
  authReady = true;
  console.log('[AUTH] Authentication ready via form, client_id:', getAuthClientId());
  authReadyResolve(); // Signal that auth is ready
  
  statusEl.textContent = 'Signed in successfully';
  statusEl.style.color = 'var(--positive)';
}

export async function handleCreateAccount(email, password) {
  if (!email || !password) {
    throw new Error('Please enter both email and password');
  }
  
  if (password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }
  
  const statusEl = document.getElementById('status');
  statusEl.textContent = 'Creating account...';
  statusEl.style.color = 'var(--muted)';
  
  await createAccountWithEmail(email, password);
  
  // Set up auth functions for the UI
  window.footcheckAuth = {
    getClientId: getAuthClientId,
    getAccessToken: getAccessToken
  };
  
  authReady = true;
  console.log('[AUTH] Authentication ready via account creation, client_id:', getAuthClientId());
  authReadyResolve(); // Signal that auth is ready
  
  statusEl.textContent = 'Account created and signed in successfully';
  statusEl.style.color = 'var(--positive)';
}

export async function handleGoogleSignIn() {
  const statusEl = document.getElementById('status');
  statusEl.textContent = 'Redirecting to Google...';
  statusEl.style.color = 'var(--muted)';
  
  await signInWithGoogle();
  // After redirect return, auth will be handled by startFootcheckApp
  // Page will redirect, so this won't return normally
}

export { getAuthClientId, getAccessToken };

